import React, { useEffect, useMemo, useState } from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import {
  asyncPostData,
  asyncPutData,
  uploadFile,
} from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import {
  Avatar,
  Box,
  Grid2,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import TextEditor from "../../text-editor/TextEditor";
import SelectInput from "../../input/SelectInput";
import { POST_MODES } from "../../../../utils/ui.constants";
import ColorPicker from "../../color-picker";
import {
  generateLinkImage,
  getPlainText,
  stripHTML,
} from "../../../../utils/helpers";
import TextInput from "../../input/TextInput";
import { cloneDeep, isArray } from "lodash";
import { v4 } from "uuid";
import { LuXCircle } from "react-icons/lu";
import CheckboxInput from "../../input/CheckboxInput";

const schema = yup.object({});

const videoTypes = [
  "video/mp4",
  "video/avi",
  "video/mov",
  "video/mpeg",
  "video",
];

const MAX_CONTENT_LENGTH = 500;

function FormFeed({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const { showAlert } = useAlertContext();
  const app = useSelector((state: any) => state.app.data);
  const { token, profile } = useSelector((state: any) => state.auth);
  const [tags, setTags] = useState<any[]>([]);
  const [images, setImages] = useState<File[]>();
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [video, setVideo] = useState<File>();
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [defaulImages, setDefaultImages] = useState<any[]>(
    defaultValues?.photos || []
  );
  const defaultData: { [key: string]: any } = {
    description: "",
    background: "",
    mode: "public",
    title: "", // link video youtube
    status: true,
  };
  const originData = defaultValues
    ? {
        ...defaultData,
        ...defaultValues,
        title: defaultValues?.exfields?.title || "",
      }
    : defaultData;
  const {
    control,
    register,
    watch,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const description = watch("description");

  const deleteDefaultImage = (index: number) => {
    if (!defaulImages) return;
    const imagesClone = cloneDeep(defaulImages);
    const newImages: any[] = [];
    for (let i = 0; i < imagesClone.length; i++) {
      if (index !== i) {
        newImages.push(imagesClone[i]);
      }
    }
    setDefaultImages(newImages);
  };

  const deleteImage = (index: number) => {
    if (!images) return;
    const imagesClone = cloneDeep(images);
    const newImages: File[] = [];
    for (let i = 0; i < imagesClone.length; i++) {
      if (index !== i) {
        newImages.push(imagesClone[i]);
      }
    }
    setImages(newImages);
  };

  const onVideoChange = (files: FileList | undefined) => {
    if (!files) return;
    const file = files[0];
    if (file) {
      const MAX_SIZE_MB = 10;
      const fileSizeMB = file.size / (1024 * 1024); // Chuyển đổi dung lượng sang MB
      if (!videoTypes.includes(file.type)) {
        showAlert({ type: "warning", message: "File không đúng định dạng" });
      } else if (fileSizeMB > MAX_SIZE_MB) {
        showAlert({
          type: "warning",
          message: `Kích thước file vượt quá ${MAX_SIZE_MB}MB`,
        }); // Reset input
      } else {
        setVideo(file);
        setImages([]);
        setImageUrls([]);
      }
    }
  };

  const plainText = useMemo(() => {
    return getPlainText(stripHTML(description).trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description]);

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { title, ...fields } = values;
    const result = {
      ...fields,
      photos: [...defaulImages],
      tags,
      exfields: {
        ...(defaultValues?.exfields || {}),
        title,
      },
    };
    // save image
    if (isArray(images) && images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const formData = new FormData();
        formData.append("file", images[i]);
        const resp = await uploadFile({
          formData,
          folder: profile?.email,
          token,
        });
        result.photos.push({
          ...resp?.data,
          url: generateLinkImage(resp?.data?.fileUrl),
        });
      }
    }
    // save video
    if (video) {
      const formData = new FormData();
      formData.append("file", video);
      const resp = await uploadFile({
        formData,
        folder: profile?.email,
        token,
      });
      result.exfields.video_url = generateLinkImage(resp?.data?.fileUrl);
    }
    return result;
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    const allow =
      !!stripHTML(dataPost.description).trim() ||
      !!dataPost.title ||
      (images?.length || 0) > 0;

    if (!allow) {
      return;
    }
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  useEffect(() => {
    if (video) {
      const url = URL.createObjectURL(video);
      setVideoUrl(url);
    } else {
      setVideoUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video]);

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  useEffect(() => {
    if (!!images && images.length > 0) {
      let urls = [];
      for (let i = 0; i < images.length; i++) {
        urls.push(URL.createObjectURL(images[i]));
      }
      setImageUrls(urls);
    } else {
      setImageUrls([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  useEffect(() => {
    return () => {
      imageUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  return (
    <ModalForm
      formWidth="800px"
      formTitle="feed"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Box>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Controller
              control={control}
              name="mode"
              render={({ field: { value, onChange } }) => {
                return (
                  <SelectInput
                    label="Chế độ"
                    options={POST_MODES}
                    defaultValue={value}
                    value={value}
                    onChange={onChange}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 7 }}>
            <TextInput
              multiline
              name="title"
              minRows={1}
              label="Link youtube"
              placeholder="Nhập link video youtube"
              register={register}
            />
          </Grid2>
          {/* Hinh anh */}
          <Grid2 size={{ xs: 6 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography>Hình ảnh</Typography>
              <Box
                key={v4()}
                component="input"
                type="file"
                multiple={true}
                accept="image/*"
                onChange={(e) => {
                  const files = e.target.files;
                  setImages([...(images || []), ...Array.from(files || [])]);
                  setVideo(undefined);
                }}
              ></Box>
            </Stack>
            <Box sx={{ pt: 1 }}>
              <Grid2 container spacing={1}>
                {isArray(defaulImages) &&
                  defaulImages.map((img, index) => {
                    return (
                      <Grid2 key={v4()} size={{ xs: 6 }}>
                        <Box sx={{ position: "relative" }}>
                          <IconButton
                            onClick={() => deleteDefaultImage(index)}
                            sx={{
                              position: "absolute",
                              zIndex: 10,
                              top: "5px",
                              right: "5px",
                              backgroundColor: "background.paper",
                              color: "text.primary",
                              boxShadow: "0px 0px 5px 1px #00000055",
                              "&:hover": {
                                backgroundColor: "background.paper",
                              },
                            }}
                          >
                            <LuXCircle size={20} />
                          </IconButton>
                          <Avatar
                            src={img?.url || ""}
                            sx={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                              borderRadius: "10px",
                              border: "2px solid",
                              borderColor: "divider",
                            }}
                          />
                        </Box>
                      </Grid2>
                    );
                  })}
                {isArray(images) &&
                  images.length > 0 &&
                  images.map((media, index) => {
                    return (
                      <Grid2 key={v4()} size={{ xs: 6 }}>
                        <Box sx={{ position: "relative" }}>
                          <IconButton
                            onClick={() => deleteImage(index)}
                            sx={{
                              position: "absolute",
                              zIndex: 10,
                              top: "5px",
                              right: "5px",
                              backgroundColor: "background.paper",
                              color: "text.primary",
                              boxShadow: "0px 0px 5px 1px #00000055",
                              "&:hover": {
                                backgroundColor: "background.paper",
                              },
                            }}
                          >
                            <LuXCircle size={20} />
                          </IconButton>
                          <Avatar
                            src={imageUrls[index]}
                            sx={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                              borderRadius: "10px",
                              border: "2px solid",
                              borderColor: "divider",
                            }}
                          />
                        </Box>
                      </Grid2>
                    );
                  })}
              </Grid2>
            </Box>
          </Grid2>
          {/* Video */}
          <Grid2 size={{ xs: 6 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography>Video</Typography>
              <Box
                key={v4()}
                component="input"
                type="file"
                multiple={true}
                accept="video/*"
                onChange={(e) => {
                  const files = e.target.files;
                  onVideoChange(files as FileList);
                  setImages(undefined);
                }}
              ></Box>
            </Stack>
            {videoUrl && (
              <Box sx={{ px: 2 }}>
                <Box
                  sx={{
                    position: "relative",
                    width: "200px",
                    height: "300px",
                    borderRadius: "10px",
                  }}
                >
                  <IconButton
                    onClick={() => setVideo(undefined)}
                    sx={{
                      position: "absolute",
                      zIndex: 10,
                      top: "5px",
                      right: "5px",
                      backgroundColor: "background.paper",
                      color: "text.primary",
                      boxShadow: "0px 0px 5px 1px #00000055",
                      "&:hover": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  >
                    <LuXCircle size={20} />
                  </IconButton>
                  <Box
                    component="video"
                    src={videoUrl}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  ></Box>
                </Box>
              </Box>
            )}
          </Grid2>
          {/* Mau nen va tich */}
          <Grid2 size={{ xs: 12 }}>
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              sx={{ width: " 100%" }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography>Màu nền</Typography>
                <Controller
                  name="background"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return <ColorPicker value={value} onChange={onChange} />;
                  }}
                />
              </Stack>
              <Box>
                <Controller
                  name="isPopular"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <CheckboxInput
                        label="Nổi bật"
                        checked={value}
                        onChange={onChange}
                      />
                    );
                  }}
                />
              </Box>
              <Box>
                <Controller
                  name="status"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <CheckboxInput
                        label="Hiển thị"
                        checked={value}
                        onChange={onChange}
                      />
                    );
                  }}
                />
              </Box>
            </Stack>
          </Grid2>
          {/* Noi dung */}
          <Grid2 size={{ xs: 12 }}>
            <Stack gap={0.5}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography sx={{ fontWeight: 500 }}>Nội dung</Typography>
                <Typography sx={{ opacity: 0.6 }}>
                  {description === "" ? 0 : plainText?.length}/
                  {MAX_CONTENT_LENGTH}
                </Typography>
              </Stack>
              <Box
                sx={{
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                }}
              >
                <Controller
                  control={control}
                  name="description"
                  render={({ field: { value, onChange } }) => {
                    return (
                      <TextEditor
                        value={value}
                        onChange={(val, tags) => {
                          onChange(val);
                          setTags(tags || []);
                        }}
                        maxLength={MAX_CONTENT_LENGTH}
                        placeholder="Nhập nội dung feed"
                      />
                    );
                  }}
                />
              </Box>
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
    </ModalForm>
  );
}

export default FormFeed;
